import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";

const resolveUrl = (url) => (url?.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

export default function AdminReports() {
  const [reports, setReports] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  const load = () =>
    api.get("/reports")
      .then((res) => setReports(res.data))
      .catch((e) => (e.response?.status === 403 ? setForbidden(true) : setReports([])));

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/reports/${id}`, { status });
      toast.success(status === "resolved" ? "Marked resolved" : "Reopened");
      load();
    } catch {
      toast.error("Couldn't update the report");
    }
  };

  if (forbidden) {
    return (
      <div className="min-h-screen bg-[#05060f] text-white flex items-center justify-center">
        <p data-testid="admin-reports-forbidden" className="text-zinc-500">Admin access only.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05060f] text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <h1 data-testid="admin-reports-title" className="font-display text-4xl font-bold mb-2 flex items-center gap-3">
          <Flag className="w-7 h-7 text-red-400" /> Reports
        </h1>
        <p className="text-zinc-400 text-sm mb-8">Images flagged by the community.</p>

        {reports === null ? (
          <p className="text-zinc-600 text-sm animate-pulse">Loading...</p>
        ) : reports.length === 0 ? (
          <p data-testid="admin-reports-empty" className="text-zinc-600 text-sm py-12 text-center">No reports — all clean.</p>
        ) : (
          <div className="space-y-4">
            {reports.map((r) => (
              <div key={r.id} data-testid={`admin-report-${r.id}`} className={`border rounded-xl p-4 flex gap-4 ${r.status === "open" ? "border-red-400/30 bg-red-500/5" : "border-white/10 bg-[#111113] opacity-60"}`}>
                <div className="w-16 shrink-0">
                  {r.image_url ? (
                    <img src={resolveUrl(r.image_url)} alt={r.card_name} className="w-16 aspect-[3/4] object-cover rounded-lg border border-white/10" />
                  ) : (
                    <div className="w-16 aspect-[3/4] border border-white/10 rounded-lg" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{r.card_name || "Unknown card"}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    <span className="text-red-300 uppercase tracking-wide">{r.reason.replace(/_/g, " ")}</span>
                    {" · "}reported by {r.reporter || "?"} · owner {r.owner || "?"}
                    {" · "}{new Date(r.created_at).toLocaleString()}
                  </p>
                  {r.details && <p className="text-xs text-zinc-500 mt-1.5">"{r.details}"</p>}
                </div>
                <div className="shrink-0 self-start">
                  {r.status === "open" ? (
                    <Button data-testid={`admin-report-resolve-${r.id}`} size="sm" onClick={() => setStatus(r.id, "resolved")} className="bg-emerald-600 hover:bg-emerald-500 rounded-full">
                      Resolve
                    </Button>
                  ) : (
                    <Button data-testid={`admin-report-reopen-${r.id}`} size="sm" variant="outline" onClick={() => setStatus(r.id, "open")} className="border-white/20 rounded-full">
                      Reopen
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
